import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, Clock, CheckCircle2, XCircle, ImagePlus, AlertTriangle } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const MOTIVOS_RECUSA = [
  { codigo: "uso_indevido", label: "Uso indevido do produto" },
  { codigo: "fora_do_prazo", label: "Fora do prazo de garantia (30 dias)" },
  { codigo: "defeito_fabricacao", label: "Defeito de fabricação — não coberto pela garantia de montagem" },
  { codigo: "evidencias_insuficientes", label: "Evidências insuficientes para comprovar o problema" },
  { codigo: "dano_terceiros", label: "Dano causado por terceiros ou transporte" },
];

const TABS = [
  { key: "em_analise", label: "Em análise" },
  { key: "complemento_solicitado", label: "Aguardando complemento" },
  { key: "aprovada", label: "Aprovadas" },
  { key: "recusada", label: "Recusadas" },
];

function horasRestantes(prazoIso) {
  if (!prazoIso) return null;
  const ms = new Date(prazoIso) - new Date();
  return Math.floor(ms / 3600000);
}

function PrazoChip({ prazoIso }) {
  const h = horasRestantes(prazoIso);
  if (h === null) return null;
  const estourado = h < 0;
  const critico = h >= 0 && h < 12;
  const cls = estourado ? "bg-red-600 text-white"
    : critico ? "bg-red-100 text-red-700 border border-red-300"
    : "bg-amber-100 text-amber-800 border border-amber-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded ${cls}`}
      data-testid="garantia-prazo-chip">
      <Clock size={12} />
      {estourado ? "PRAZO ESTOURADO" : `${h}h restantes`}
    </span>
  );
}

export default function Garantias() {
  const [tab, setTab] = useState("em_analise");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // {tipo:"recusar"|"complemento", sol}
  const [motivoCodigo, setMotivoCodigo] = useState("");
  const [observacao, setObservacao] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (t = tab) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/garantia-solicitacoes?status=${t}`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const aprovar = async (sol) => {
    if (!window.confirm(`Aprovar a garantia ${sol.protocolo}? Um serviço de garantia sem custo será aberto para o cliente.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/garantia-solicitacoes/${sol.id}/aprovar`, {});
      toast.success(`Garantia aprovada · ${r.data.pim_code}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const recusar = async () => {
    if (!motivoCodigo) { toast.error("Selecione o motivo padronizado da recusa"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/garantia-solicitacoes/${modal.sol.id}/recusar`,
        { motivo_codigo: motivoCodigo, observacao });
      toast.success("Solicitação recusada — cliente notificado");
      setModal(null); setMotivoCodigo(""); setObservacao("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const pedirComplemento = async () => {
    if (mensagem.trim().length < 5) { toast.error("Descreva o que o cliente precisa enviar"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/garantia-solicitacoes/${modal.sol.id}/pedir-complemento`, { mensagem });
      toast.success("Complemento solicitado — cliente notificado por WhatsApp");
      setModal(null); setMensagem("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-8" data-testid="admin-garantias-page">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="label-cap">Pós-venda</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1 flex items-center gap-2">
            <ShieldCheck size={26} className="text-[#FF6700]" /> Garantias
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solicitações do cliente com fotos e vídeo · decisão em até <strong>72 horas</strong>.
            Alertamos a equipe automaticamente quando faltam menos de 12h.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="garantias-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border transition-colors ${tab === t.key
              ? "bg-[#142766] text-white border-[#142766]"
              : "bg-white border-border text-muted-foreground hover:border-[#142766]"}`}
            data-testid={`garantias-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Carregando…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground"
          data-testid="garantias-vazio">
          Nenhuma solicitação de garantia nesta etapa.
        </div>
      )}

      <div className="space-y-4">
        {items.map(sol => (
          <div key={sol.id} className="border border-border bg-white p-5" data-testid={`garantia-card-${sol.protocolo}`}>
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-sm">{sol.protocolo}</span>
                  {sol.status === "em_analise" && <PrazoChip prazoIso={sol.prazo_analise} />}
                  {sol.status === "complemento_solicitado" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded bg-blue-100 text-blue-700 border border-blue-300">
                      <ImagePlus size={12} /> Aguardando cliente
                    </span>
                  )}
                  {sol.status === "aprovada" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded bg-emerald-100 text-emerald-700 border border-emerald-300">
                      <CheckCircle2 size={12} /> Aprovada · {sol.garantia_pim}
                    </span>
                  )}
                  {sol.status === "recusada" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold rounded bg-red-100 text-red-700 border border-red-300">
                      <XCircle size={12} /> Recusada
                    </span>
                  )}
                </div>
                <div className="text-sm mt-1 font-semibold">{sol.cliente_nome} · OS {sol.servico_pim || "—"}</div>
                <div className="text-xs text-muted-foreground">{sol.produto} · solicitado em {new Date(sol.criado_em).toLocaleString("pt-BR")}</div>
              </div>
              {sol.status === "em_analise" && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" disabled={busy} onClick={() => aprovar(sol)}
                    className="btn-sharp bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`garantia-aprovar-${sol.protocolo}`}>
                    <CheckCircle2 size={15} /> Aprovar
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => { setModal({ tipo: "complemento", sol }); setMensagem(""); }}
                    className="btn-sharp"
                    data-testid={`garantia-complemento-${sol.protocolo}`}>
                    <ImagePlus size={15} /> Pedir complemento
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => { setModal({ tipo: "recusar", sol }); setMotivoCodigo(""); setObservacao(""); }}
                    className="btn-sharp border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`garantia-recusar-${sol.protocolo}`}>
                    <XCircle size={15} /> Recusar
                  </Button>
                </div>
              )}
            </div>

            <div className="mt-3 text-sm bg-slate-50 border border-border p-3">
              <span className="label-cap block mb-1">Problema relatado pelo cliente</span>
              "{sol.descricao}"
            </div>

            {sol.status === "complemento_solicitado" && sol.complemento_mensagem && (
              <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 p-2">
                <strong>Complemento pedido:</strong> {sol.complemento_mensagem}
              </div>
            )}
            {sol.status === "recusada" && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 p-2">
                <strong>Motivo:</strong> {sol.motivo_label || sol.motivo_recusa}
                {sol.observacao_recusa && <> · {sol.observacao_recusa}</>}
              </div>
            )}
            {(sol.complementos || []).length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                {sol.complementos.map((c, i) => (
                  <div key={i}>↻ Complemento enviado em {new Date(c.enviado_em).toLocaleString("pt-BR")} · {c.fotos} foto(s){c.video ? " + 1 vídeo" : ""}{c.descricao ? ` · "${c.descricao}"` : ""}</div>
                ))}
              </div>
            )}

            <div className="mt-3">
              <span className="label-cap block mb-2">Evidências ({sol.fotos_count || 0} foto(s) · {sol.video_count || 0} vídeo(s))</span>
              <div className="flex gap-2 flex-wrap" data-testid={`garantia-evidencias-${sol.protocolo}`}>
                {(sol.evidencias || []).map(ev => {
                  const url = `${BASE}/api/files/${sol.servico_id}/${ev.categoria}/${(ev.path || "").split("/").pop()}?s=${ev.sig || ""}`;
                  const isVideo = (ev.content_type || "").startsWith("video/") || ev.categoria.includes("video");
                  return isVideo ? (
                    <video key={ev.id} src={url} controls preload="metadata"
                      className="h-32 border border-border bg-black" data-testid="garantia-evidencia-video" />
                  ) : (
                    <a key={ev.id} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="Evidência da garantia"
                        className="h-32 w-32 object-cover border border-border hover:opacity-80 transition-opacity"
                        data-testid="garantia-evidencia-foto" loading="lazy" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="garantia-modal">
          {modal?.tipo === "recusar" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle size={18} className="text-red-600" /> Recusar garantia {modal.sol.protocolo}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-cap">Motivo padronizado *</Label>
                  <select value={motivoCodigo} onChange={(e) => setMotivoCodigo(e.target.value)}
                    className="mt-1 w-full border border-border bg-white p-2 text-sm"
                    data-testid="garantia-recusa-motivo-select">
                    <option value="">Selecione o motivo…</option>
                    {MOTIVOS_RECUSA.map(m => <option key={m.codigo} value={m.codigo}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="label-cap">Observação para o cliente (opcional)</Label>
                  <Textarea rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)}
                    placeholder="Detalhe o que foi identificado nas evidências…"
                    data-testid="garantia-recusa-observacao" />
                </div>
                <p className="text-xs text-muted-foreground">
                  O cliente será notificado por WhatsApp e verá o motivo na página de acompanhamento,
                  com opção de falar com a equipe pelo chat.
                </p>
                <Button onClick={recusar} disabled={busy}
                  className="btn-sharp w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="garantia-recusa-confirmar">
                  <XCircle size={15} /> Confirmar recusa
                </Button>
              </div>
            </>
          )}
          {modal?.tipo === "complemento" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ImagePlus size={18} className="text-blue-600" /> Pedir complemento · {modal.sol.protocolo}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-cap">O que o cliente precisa enviar? *</Label>
                  <Textarea rows={3} value={mensagem} onChange={(e) => setMensagem(e.target.value)}
                    placeholder="Ex.: envie uma foto aproximada da dobradiça e um vídeo abrindo e fechando a porta…"
                    data-testid="garantia-complemento-mensagem" />
                </div>
                <p className="text-xs text-muted-foreground">
                  O cliente será avisado por WhatsApp e poderá reenviar novas fotos/vídeo pela página
                  de acompanhamento. O prazo de 72h recomeça após o envio.
                </p>
                <Button onClick={pedirComplemento} disabled={busy}
                  className="btn-sharp w-full" data-testid="garantia-complemento-confirmar">
                  <ImagePlus size={15} /> Solicitar complemento
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
